import React, { createContext, useContext, useEffect, useState } from "react";
import api, {
  setAuthTokens,
  clearStoredToken,
  getStoredToken,
  getStoredRefreshToken,
  setStoredToken,
  refreshAccessToken,
} from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null=checking, false=unauth, object=authed
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    let token = getStoredToken();
    if (!token && getStoredRefreshToken()) {
      try {
        token = await refreshAccessToken();
      } catch {
        clearStoredToken();
        setUser(false);
        setLoading(false);
        return;
      }
    }
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    // Keep cookie/storage in sync so Google OAuth return stays logged in.
    setStoredToken(token, { remember: true });
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      if (getStoredRefreshToken()) {
        try {
          await refreshAccessToken();
          const { data } = await api.get("/auth/me");
          setUser(data);
          return;
        } catch {
          /* fall through */
        }
      }
      clearStoredToken();
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const login = async (email, password, remember = true) => {
    const { data } = await api.post("/auth/login", { email, password });
    setAuthTokens(
      { access_token: data.access_token, refresh_token: data.refresh_token },
      { remember },
    );
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) {}
    clearStoredToken();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
